/**
 * API service layer.
 * Wraps Supabase queries for each entity.
 */

import { getSupabase } from './supabase.js';

// ── Aircraft ──────────────────────────────────────────

export async function getAircraft() {
  const { data, error } = await getSupabase()
    .from('aircraft')
    .select('*')
    .order('registration');
  if (error) throw error;
  return data;
}

export async function getAircraftById(id) {
  const { data, error } = await getSupabase()
    .from('aircraft')
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

// ── Flights ───────────────────────────────────────────

export async function getFlights(filters = {}) {
  let query = getSupabase()
    .from('flights')
    .select('*, aircraft(registration, type), pilots!pilot_id(name)')
    .order('flight_date', { ascending: filters.ascending ?? false });

  if (filters.pilot_id) query = query.eq('pilot_id', filters.pilot_id);
  if (filters.aircraft_id) query = query.eq('aircraft_id', filters.aircraft_id);
  if (filters.from) query = query.gte('flight_date', filters.from);
  if (filters.to) query = query.lte('flight_date', filters.to);
  if (filters.status) query = query.eq('status', filters.status);
  if (filters.statusIn) query = query.in('status', filters.statusIn);

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

/**
 * Fetch a single flight by ID.
 */
export async function getFlightById(id) {
  const { data, error } = await getSupabase()
    .from('flights')
    .select('*, aircraft(registration, type, fuel_tank_config, oil_measurement_unit), pilots!pilot_id(name)')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

/**
 * Fetch flights with booking-phase statuses (for calendar/booking views).
 */
export async function getBookingFlights(filters = {}) {
  let query = getSupabase()
    .from('flights')
    .select('*, aircraft(registration, type), pilots!pilot_id(name)')
    .in('status', ['SUBMITTED', 'APPROVED', 'FLOWN'])
    .order('flight_date', { ascending: true });

  if (filters.aircraft_id) query = query.eq('aircraft_id', filters.aircraft_id);
  if (filters.pilot_id) query = query.eq('pilot_id', filters.pilot_id);

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

/**
 * Transition a flight's status with optional meta merge (e.g. reject_reason).
 */
export async function transitionFlightStatus(id, newStatus, meta = {}) {
  const updates = { status: newStatus, updated_at: new Date().toISOString() };
  if (Object.keys(meta).length > 0) {
    const { data: existing } = await getSupabase()
      .from('flights').select('meta').eq('id', id).single();
    updates.meta = { ...(existing?.meta || {}), ...meta };
  }
  const { data, error } = await getSupabase()
    .from('flights').update(updates).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

/**
 * Fetch APPROVED flights past their date with no flight log (needs completion).
 */
export async function getFlightsNeedingLog(pilotId) {
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await getSupabase()
    .from('flights')
    .select('*, aircraft(registration, type)')
    .eq('pilot_id', pilotId)
    .eq('status', 'APPROVED')
    .lte('flight_date', today)
    .is('flight_time_mins', null)
    .order('flight_date', { ascending: false });
  if (error) throw error;
  return data;
}

export async function createFlight(flight) {
  const { data, error } = await getSupabase()
    .from('flights')
    .insert(flight)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Update an existing flight record (admin amendment).
 * @param {string} flightId - The flight UUID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated flight record
 */
export async function updateFlight(flightId, updates) {
  const { data, error } = await getSupabase()
    .from('flights')
    .update(updates)
    .eq('id', flightId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Fuel & Oil ────────────────────────────────────────

export async function createFuelOil(record) {
  const { data, error } = await getSupabase()
    .from('flight_fuel_oil')
    .insert(record)
    .select()
    .single();
  if (error) throw error;
  return data;
}
// ── Flight Expenses ───────────────────────────────────

/**
 * Create a single reimbursable expense for a flight.
 * @param {string} flightId - The flight UUID
 * @param {{ description: string, amount: number }} expense - Expense data
 * @returns {Promise<Object>} Created expense record
 */
export async function createExpense(flightId, expense) {
  const { data, error } = await getSupabase()
    .from('flight_expenses')
    .insert({ flight_id: flightId, description: expense.description, amount: expense.amount })
    .select()
    .single();
  if (error) throw error;
  return data;
}



export async function getLastFuelOil(aircraftId) {
  const { data, error } = await getSupabase()
    .from('flight_fuel_oil')
    .select('*, flights!inner(aircraft_id, flight_date)')
    .eq('flights.aircraft_id', aircraftId)
    .order('flights(flight_date)', { ascending: false })
    .limit(1)
    .single();
  if (error && error.code !== 'PGRST116') throw error;
  return data;
}

// ── Defects ───────────────────────────────────────────

export async function getOpenDefects(aircraftId) {
  let query = getSupabase()
    .from('defects')
    .select('*, reported_by:pilots!reported_by(name)')
    .eq('status', 'OPEN')
    .order('opened_at', { ascending: false });

  if (aircraftId) query = query.eq('aircraft_id', aircraftId);

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function createDefect(defect) {
  const { serviceability, ...defectRow } = defect;
  const supabase = getSupabase();

  const { data, error } = await supabase
    .from('defects')
    .insert(defectRow)
    .select()
    .single();
  if (error) throw error;

  // Transactional aircraft status update if unserviceable (Req 14.2)
  if (serviceability === 'UNSERVICEABLE') {
    const { error: statusError } = await supabase
      .from('aircraft')
      .update({ status: 'UNSERVICEABLE', updated_at: new Date().toISOString() })
      .eq('id', defectRow.aircraft_id);
    if (statusError) throw statusError;
  }

  return data;
}

export async function getAllDefects() {
  const { data, error } = await getSupabase()
    .from('defects')
    .select('*, reported_by:pilots!reported_by(name), aircraft(id, registration, type)')
    .order('opened_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function updateDefect(defectId, updates) {
  const { data, error } = await getSupabase()
    .from('defects')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', defectId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateDefectStatus(defectId, status) {
  return updateDefect(defectId, { status });
}

export async function archiveDefect(defectId) {
  return updateDefect(defectId, { archived: true });
}

export async function deleteDefect(defectId) {
  const { error } = await getSupabase()
    .from('defects')
    .delete()
    .eq('id', defectId);
  if (error) throw error;
}

// ── Billing ───────────────────────────────────────────

/**
 * Fetch billing records (one per billed flight).
 *
 * Historical note: billing used to live in a separate billing_records table.
 * The flight IS the bill now — per-flight billing columns are on flights.
 * This function reshapes flights into the old record shape so downstream
 * UI code (billing-ledger, billing-summary-widget, billing pages) keeps
 * working without a rewrite.
 *
 * @param {Object} [filters]
 * @param {string} [filters.status] - UNBILLED/INVOICED/PAID/WAIVED/DISPUTE/ONHOLD
 *                                    (maps to flights.billing_status)
 */
export async function getBillingRecords(filters = {}) {
  let query = getSupabase()
    .from('flights')
    .select(`
      id, pilot_id, aircraft_id, flight_date, block_time_mins, flight_time_mins,
      billing_basis, rate_applied, amount_due, billing_status, billed_at, invoice_id,
      flight_cost, fuel_charge, oil_charge, landing_fee, misc_charge,
      pilots:pilot_id(name, role), aircraft(registration)
    `)
    .not('amount_due', 'is', null)
    .order('flight_date', { ascending: false });

  if (filters.status) query = query.eq('billing_status', filters.status);

  const { data, error } = await query;
  if (error) throw error;

  // Reshape each flight into the legacy billing_record shape expected by the
  // billing UI. The core identity of "one bill per flight-day" is preserved;
  // period_start/period_end collapse to the single flight_date.
  return (data || []).map((f) => ({
    id: f.id,
    pilot_id: f.pilot_id,
    aircraft_id: f.aircraft_id,
    period_start: f.flight_date,
    period_end: f.flight_date,
    total_billable_mins: f.billing_basis === 'FLIGHT_TIME'
      ? (f.flight_time_mins ?? 0)
      : (f.block_time_mins ?? 0),
    billing_basis: f.billing_basis,
    rate_applied: f.rate_applied,
    amount_due: f.amount_due,
    paid_amount: 0,  // Per-flight paid tracking is via invoice linkage, not on the flight.
    status: f.billing_status,     // UNBILLED/INVOICED/PAID/...
    invoice_id: f.invoice_id,
    billed_at: f.billed_at,
    flight_id: f.id,              // Convenience for linking back to the flight
    flight_cost: f.flight_cost,
    fuel_charge: f.fuel_charge,
    oil_charge: f.oil_charge,
    landing_fee: f.landing_fee,
    misc_charge: f.misc_charge,
    pilots: f.pilots,
    aircraft: f.aircraft,
  }));
}


// ── Airports ──────────────────────────────────────────

export async function getAircraftAirports(aircraftId) {
  const { data, error } = await getSupabase()
    .from('aircraft_airports')
    .select('*')
    .eq('aircraft_id', aircraftId)
    .order('icao_code');
  if (error) throw error;
  return data;
}

export async function upsertAircraftAirports(aircraftId, codes) {
  const supabase = getSupabase();

  // Delete existing entries for this aircraft
  const { error: deleteError } = await supabase
    .from('aircraft_airports')
    .delete()
    .eq('aircraft_id', aircraftId);
  if (deleteError) throw deleteError;

  // Insert new entries
  if (codes.length === 0) return [];

  const rows = codes.map((code) => ({ aircraft_id: aircraftId, icao_code: code }));
  const { data, error } = await supabase
    .from('aircraft_airports')
    .insert(rows)
    .select();
  if (error) throw error;
  return data;
}

export async function searchAirports(query, limit = 10) {
  const pattern = `%${query}%`;
  const { data, error } = await getSupabase()
    .from('airports')
    .select('*')
    .or(`icao_code.ilike.${pattern},name.ilike.${pattern}`)
    .limit(limit)
    .order('icao_code');
  if (error) throw error;
  return data;
}

export async function getAirports() {
  const { data, error } = await getSupabase()
    .from('airports')
    .select('*')
    .order('icao_code');
  if (error) throw error;
  return data;
}

export async function upsertAirport(airport) {
  const { data, error } = await getSupabase()
    .from('airports')
    .upsert(airport, { onConflict: 'icao_code' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteAirport(id) {
  const { error } = await getSupabase()
    .from('airports')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ── Aircraft Details ──────────────────────────────────

export async function updateAircraftDetails(id, details) {
  const { data, error } = await getSupabase()
    .from('aircraft')
    .update(details)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Pilot Licences ────────────────────────────────────

export async function getPilotLicences(pilotId) {
  const { data, error } = await getSupabase()
    .from('pilot_licences')
    .select('*')
    .eq('pilot_id', pilotId)
    .order('expiry_date', { ascending: false });
  if (error) throw error;
  return data;
}

export async function createPilotLicence(licence) {
  const { data, error } = await getSupabase()
    .from('pilot_licences')
    .insert(licence)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deletePilotLicence(id) {
  const { error } = await getSupabase()
    .from('pilot_licences')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function updatePilotLicence(id, updates) {
  const { data, error } = await getSupabase()
    .from('pilot_licences')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}


export async function getLicenceAircraftMappings() {
  const { data, error } = await getSupabase()
    .from('licence_aircraft_mappings')
    .select('*')
    .order('rating_category');
  if (error) throw error;
  return data;
}

export async function upsertLicenceAircraftMapping(mapping) {
  const { data, error } = await getSupabase()
    .from('licence_aircraft_mappings')
    .upsert(mapping, { onConflict: 'rating_category,aircraft_type' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Currency Thresholds ───────────────────────────────

export async function getCurrencyThresholds() {
  const { data, error } = await getSupabase()
    .from('currency_thresholds')
    .select('*')
    .order('aircraft_type');
  if (error) throw error;
  return data;
}

export async function upsertCurrencyThreshold(threshold) {
  const { data, error } = await getSupabase()
    .from('currency_thresholds')
    .upsert(threshold, { onConflict: 'aircraft_type' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Pilot Flying Hours ────────────────────────────────

export async function getPilotFlyingHours(pilotId) {
  const supabase = getSupabase();

  // Fetch completed flights for the pilot (FLOWN only)
  const { data: allFlights, error: allError } = await supabase
    .from('flights')
    .select('flight_time_mins, flight_date')
    .eq('pilot_id', pilotId)
    .eq('status', 'FLOWN');
  if (allError) throw allError;

  const now = new Date();
  const ninetyDaysAgo = new Date(now);
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
  const cutoff = ninetyDaysAgo.toISOString().split('T')[0];

  const totalMins = (allFlights || []).reduce((sum, f) => sum + (f.flight_time_mins || 0), 0);
  const last90Mins = (allFlights || [])
    .filter((f) => f.flight_date >= cutoff)
    .reduce((sum, f) => sum + (f.flight_time_mins || 0), 0);

  return {
    total_minutes: totalMins,
    last_90_days_minutes: last90Mins,
  };
}

// ── Billing Rates ─────────────────────────────────────

export async function getBillingRates() {
  const { data, error } = await getSupabase()
    .from('billing_rates')
    .select('*, aircraft(registration)')
    .order('aircraft_id');
  if (error) throw error;
  return data;
}

export async function updateBillingRate(id, updates) {
  const { data, error } = await getSupabase()
    .from('billing_rates')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function createBillingRate(rate) {
  const { data, error } = await getSupabase()
    .from('billing_rates')
    .insert(rate)
    .select('*, aircraft(registration)')
    .single();
  if (error) throw error;
  return data;
}

export async function deleteBillingRate(id) {
  const { error } = await getSupabase()
    .from('billing_rates')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function deleteCurrencyThreshold(id) {
  const { error } = await getSupabase()
    .from('currency_thresholds')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function deleteLicenceAircraftMapping(id) {
  const { error } = await getSupabase()
    .from('licence_aircraft_mappings')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ── Pilots (Admin) ────────────────────────────────────

export async function getPilots() {
  const { data, error } = await getSupabase()
    .from('pilots')
    .select('*, parent:parent_id(name)')
    .order('name');
  if (error) throw error;
  return data;
}

// ── Aircraft Detail Page ──────────────────────────────

export async function getFlightsForAircraft(aircraftId, limit = 20) {
  const { data, error } = await getSupabase()
    .from('flights')
    .select('*, pilots!pilot_id(name)')
    .eq('aircraft_id', aircraftId)
    .eq('status', 'FLOWN')
    .order('flight_date', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
}

export async function getUpcomingBookings(aircraftId) {
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await getSupabase()
    .from('flights')
    .select('*, pilots!pilot_id(name)')
    .eq('aircraft_id', aircraftId)
    .in('status', ['SUBMITTED', 'APPROVED'])
    .gte('flight_date', today)
    .order('flight_date', { ascending: true })
    .order('start_time', { ascending: true });
  if (error) throw error;
  return data;
}

export async function getOpenDefectsForAircraft(aircraftId) {
  const { data, error } = await getSupabase()
    .from('defects')
    .select('*, reported_by:pilots!reported_by(name)')
    .eq('aircraft_id', aircraftId)
    .eq('status', 'OPEN')
    .order('opened_at', { ascending: false });
  if (error) throw error;
  return data;
}

/**
 * Fetch all defects for an aircraft (all statuses: OPEN, UNSURE, RESOLVED).
 * @param {string} aircraftId
 * @returns {Promise<Object[]>}
 */
export async function getAllDefectsForAircraft(aircraftId) {
  const { data, error } = await getSupabase()
    .from('defects')
    .select('*, reported_by:pilots!reported_by(name)')
    .eq('aircraft_id', aircraftId)
    .order('opened_at', { ascending: false });
  if (error) throw error;
  return data;
}


export async function getMaintenanceEvents(aircraftId) {
  const { data, error } = await getSupabase()
    .from('maintenance_events')
    .select('*')
    .eq('aircraft_id', aircraftId)
    .order('date_performed', { ascending: false });
  if (error) throw error;
  return data;
}

export async function getAllMaintenanceEvents() {
  const { data, error } = await getSupabase()
    .from('maintenance_events')
    .select('*, aircraft:aircraft_id(id, registration, type)')
    .order('performed_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return data;
}

export async function getCurrentBillingRate(aircraftId) {
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await getSupabase()
    .from('billing_rates')
    .select('*')
    .eq('aircraft_id', aircraftId)
    .lte('effective_from', today)
    .order('effective_from', { ascending: false })
    .limit(1)
    .single();
  if (error && error.code !== 'PGRST116') throw error;
  return data;
}

// ── Pilot Management ──────────────────────────────────

export async function createPilotWithAuth(pilotData) {
  const supabase = getSupabase();

  // Create Supabase Auth user
  const { data: authData, error: authError } = await supabase.auth.admin.createUser({
    email: pilotData.email,
    password: pilotData.password,
    email_confirm: true,
  });
  if (authError) throw authError;

  // Create pilots table row linked to auth user
  const { data, error } = await supabase
    .from('pilots')
    .insert({
      id: authData.user.id,
      name: pilotData.name,
      email: pilotData.email,
      role: pilotData.role || 'pilot',
      active: true,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updatePilot(id, updates) {
  const { data, error } = await getSupabase()
    .from('pilots')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deactivatePilot(id) {
  return updatePilot(id, { active: false });
}

export async function reactivatePilot(id) {
  return updatePilot(id, { active: true });
}

export async function deletePilot(id) {
  const supabase = getSupabase();
  // Delete the pilots table row first (FK constraints may prevent this)
  const { error: pilotError } = await supabase
    .from('pilots')
    .delete()
    .eq('id', id);
  if (pilotError) throw pilotError;

  // Delete the Supabase Auth user
  const { error: authError } = await supabase.auth.admin.deleteUser(id);
  if (authError) throw authError;
}

export async function resetUserPassword(userId) {
  const supabase = getSupabase();
  // Look up the user's email
  const { data: pilot, error: fetchError } = await supabase
    .from('pilots')
    .select('email')
    .eq('id', userId)
    .single();
  if (fetchError) throw fetchError;

  // Send password reset email via Supabase Auth
  const { error } = await supabase.auth.resetPasswordForEmail(pilot.email);
  if (error) throw error;
}

export async function getAdminCount() {
  const { count, error } = await getSupabase()
    .from('pilots')
    .select('*', { count: 'exact', head: true })
    .eq('role', 'admin')
    .eq('active', true);
  if (error) throw error;
  return count;
}

// ── Aircraft Management ───────────────────────────────

export async function createAircraft(aircraftData) {
  const record = {
    registration: aircraftData.registration,
    type: aircraftData.type,
    name: aircraftData.name,
    status: aircraftData.status || 'SERVICEABLE',
    airframe_hours: aircraftData.airframe_hours || 0,
    engine_hours: aircraftData.engine_hours || 0,
    propeller_hours: aircraftData.propeller_hours || 0,
    total_hours: aircraftData.airframe_hours || 0,
    hours_since_last_50hr: aircraftData.hours_since_last_50hr || 0,
    fuel_tank_config: aircraftData.fuel_tank_config || {},
    oil_measurement_unit: aircraftData.oil_measurement_unit || 'quarts',
    max_oil_capacity: aircraftData.max_oil_capacity || 0,
    meta: aircraftData.meta || {},
  };

  const { data, error } = await getSupabase()
    .from('aircraft')
    .insert(record)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateAircraft(id, updates) {
  // If meta is being updated, merge with existing
  if (updates.meta) {
    const { data: existing, error: fetchError } = await getSupabase()
      .from('aircraft')
      .select('meta')
      .eq('id', id)
      .single();
    if (fetchError) throw fetchError;
    updates.meta = { ...(existing.meta || {}), ...updates.meta };
  }

  // Keep total_hours in sync with airframe_hours
  if (updates.airframe_hours !== undefined) {
    updates.total_hours = updates.airframe_hours;
  }

  const { data, error } = await getSupabase()
    .from('aircraft')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function retireAircraft(id) {
  const { data: existing, error: fetchError } = await getSupabase()
    .from('aircraft')
    .select('name, meta')
    .eq('id', id)
    .single();
  if (fetchError) throw fetchError;

  const { data, error } = await getSupabase()
    .from('aircraft')
    .update({
      status: 'UNSERVICEABLE',
      meta: { ...(existing.meta || {}), retired: true },
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Backup / Restore ──────────────────────────────────

const BACKUP_TABLES = [
  'aircraft',
  'pilots',
  'flights',                // includes per-flight billing columns (flight IS the bill)
  'flight_fuel_oil',
  'defects',
  'maintenance_events',
  'billing_rates',
  'invoices',               // statements (rolled-up flights)
  'airports',
  'aircraft_airports',
  'pilot_licences',
  'licence_aircraft_mappings',
  'currency_thresholds',
];

export async function exportAllData() {
  const supabase = getSupabase();
  const data = {};
  const record_counts = {};

  for (const table of BACKUP_TABLES) {
    const { data: rows, error } = await supabase
      .from(table)
      .select('*');
    if (error) throw new Error(`Export failed for table "${table}": ${error.message}`);
    data[table] = rows;
    record_counts[table] = rows.length;
  }

  return {
    metadata: {
      export_date: new Date().toISOString(),
      app_version: '1.2',
      record_counts,
    },
    data,
  };
}

export async function importData(tableData, tableName) {
  if (!tableData || tableData.length === 0) return [];

  const { data, error } = await getSupabase()
    .from(tableName)
    .upsert(tableData, { onConflict: 'id' })
    .select();
  if (error) throw new Error(`Import failed for table "${tableName}": ${error.message}`);
  return data;
}

// ── Airport Management ────────────────────────────────

export async function createAirport(airport) {
  const { data, error } = await getSupabase()
    .from('airports')
    .insert(airport)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateAirport(id, updates) {
  const { data, error } = await getSupabase()
    .from('airports')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Flight Log ────────────────────────────────────────

export async function getFlightWithFuelAndDefects(flightId) {
  const supabase = getSupabase();

  const { data: flight, error: flightError } = await supabase
    .from('flights')
    .select('*, aircraft(registration, type, name, fuel_tank_config, max_oil_capacity, oil_measurement_unit), pilots!pilot_id(name)')
    .eq('id', flightId)
    .single();
  if (flightError) throw flightError;

  const { data: fuelOil, error: fuelError } = await supabase
    .from('flight_fuel_oil')
    .select('*')
    .eq('flight_id', flightId)
    .single();
  if (fuelError && fuelError.code !== 'PGRST116') throw fuelError;

  const { data: defects, error: defectsError } = await supabase
    .from('defects')
    .select('*')
    .eq('flight_id', flightId);
  if (defectsError) throw defectsError;

  return { flight, fuelOil: fuelOil || null, defects: defects || [] };
}

// ── Pilot Dashboard ───────────────────────────────────

/**
 * Fetches aggregated data for the pilot dashboard.
 * Queries: pilot licences, flights (for hours/takeoffs/landings),
 * pilot_account_entries (for balance), billing_records (for period cost).
 *
 * @param {string} pilotId - The pilot's UUID
 * @returns {Promise<Object>} Dashboard data object
 */
export async function getPilotDashboardData(pilotId) {
  const supabase = getSupabase();

  // Fetch pilot licences
  const { data: licences, error: licErr } = await supabase
    .from('pilot_licences')
    .select('*')
    .eq('pilot_id', pilotId)
    .order('expiry_date', { ascending: true });
  if (licErr) throw licErr;

  // Fetch pilot profile (for total_hours, p1_hours)
  const { data: pilot, error: pilotErr } = await supabase
    .from('pilots')
    .select('name, total_hours, p1_hours')
    .eq('id', pilotId)
    .single();
  if (pilotErr) throw pilotErr;

  // Fetch all flights/bookings for the pilot (with aircraft registration)
  const { data: flights, error: flightsErr } = await supabase
    .from('flights')
    .select('id, flight_date, start_time, end_time, departure, destination, flight_time_mins, block_time_mins, takeoffs, landings, pilot_id, aircraft_id, status, purpose, aircraft(registration, type)')
    .eq('pilot_id', pilotId)
    .order('flight_date', { ascending: false });
  if (flightsErr) throw flightsErr;

  // Fetch pilot account entries (for balance)
  const { data: accountEntries, error: accErr } = await supabase
    .from('pilot_account_entries')
    .select('entry_type, amount')
    .eq('pilot_id', pilotId);
  if (accErr) throw accErr;

  // Fetch billed flights for the pilot (for period cost). The flight IS the bill.
  const { data: billedFlights, error: billErr } = await supabase
    .from('flights')
    .select('flight_date, amount_due, billing_basis, block_time_mins, flight_time_mins')
    .eq('pilot_id', pilotId)
    .not('amount_due', 'is', null);
  if (billErr) throw billErr;

  // Reshape into the legacy period-record shape so billing-summary-widget
  // (computePeriodCost) works unchanged.
  const billingRecords = (billedFlights || []).map((f) => ({
    period_start: f.flight_date,
    period_end: f.flight_date,
    amount_due: f.amount_due,
    total_billable_mins: f.billing_basis === 'FLIGHT_TIME'
      ? (f.flight_time_mins ?? 0)
      : (f.block_time_mins ?? 0),
  }));

  // Fetch flight expenses for the pilot's flights in the current period
  // (for landing fees computation)
  const now = new Date();
  const periodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

  const currentPeriodFlightIds = (flights || [])
    .filter(f => f.status === 'FLOWN' && f.flight_date >= periodStart && f.flight_date <= periodEnd)
    .map(f => f.id);

  let landingFees = 0;
  if (currentPeriodFlightIds.length > 0) {
    const { data: expenses, error: expErr } = await supabase
      .from('flight_expenses')
      .select('amount, description')
      .in('flight_id', currentPeriodFlightIds);
    if (expErr) throw expErr;

    // Sum expenses that look like landing fees
    landingFees = (expenses || [])
      .filter(e => (e.description || '').toLowerCase().includes('landing'))
      .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  }

  // Map flights for recent flights widget (flatten aircraft registration)
  const recentFlights = (flights || []).map(f => ({
    ...f,
    aircraft_registration: f.aircraft?.registration || '—',
  }));

  return {
    licences: licences || [],
    pilot,
    flights: recentFlights,
    accountEntries: accountEntries || [],
    billingRecords: billingRecords || [],
    landingFees,
  };
}

// ── Pilot Billing ─────────────────────────────────────

/**
 * Fetch a pilot's billing history: billing records (with credits/debits)
 * and account entries, optionally filtered by date range.
 *
 * @param {string} pilotId
 * @param {{ start?: string, end?: string }} [dateRange] - YYYY-MM-DD bounds (inclusive)
 * @returns {Promise<{ billingRecords: Object[], accountEntries: Object[] }>}
 * Requirements: 15.1, 15.2, 15.3
 */
export async function getPilotBillingHistory(pilotId, dateRange) {
  const supabase = getSupabase();

  // Billed flights for this pilot (the flight IS the bill).
  let brQuery = supabase
    .from('flights')
    .select(`
      id, pilot_id, aircraft_id, flight_date, block_time_mins, flight_time_mins,
      billing_basis, rate_applied, amount_due, billing_status, billed_at, invoice_id,
      flight_cost, fuel_charge, oil_charge, landing_fee, misc_charge,
      pilots:pilot_id(name, role), aircraft(registration)
    `)
    .eq('pilot_id', pilotId)
    .not('amount_due', 'is', null)
    .order('flight_date', { ascending: false });

  if (dateRange?.start) brQuery = brQuery.gte('flight_date', dateRange.start);
  if (dateRange?.end) brQuery = brQuery.lte('flight_date', dateRange.end);

  const { data: flightRows, error: brErr } = await brQuery;
  if (brErr) throw brErr;

  // Reshape into the legacy billing_record shape for downstream UI.
  const billingRecords = (flightRows || []).map((f) => ({
    id: f.id,
    pilot_id: f.pilot_id,
    aircraft_id: f.aircraft_id,
    period_start: f.flight_date,
    period_end: f.flight_date,
    total_billable_mins: f.billing_basis === 'FLIGHT_TIME'
      ? (f.flight_time_mins ?? 0)
      : (f.block_time_mins ?? 0),
    billing_basis: f.billing_basis,
    rate_applied: f.rate_applied,
    amount_due: f.amount_due,
    paid_amount: 0,
    status: f.billing_status,
    invoice_id: f.invoice_id,
    billed_at: f.billed_at,
    flight_id: f.id,
    flight_cost: f.flight_cost,
    fuel_charge: f.fuel_charge,
    oil_charge: f.oil_charge,
    landing_fee: f.landing_fee,
    misc_charge: f.misc_charge,
    pilots: f.pilots,
    aircraft: f.aircraft,
  }));

  // Account entries for this pilot
  let aeQuery = supabase
    .from('pilot_account_entries')
    .select('entry_type, amount, description, entry_date, created_by')
    .eq('pilot_id', pilotId)
    .order('entry_date', { ascending: false });

  if (dateRange?.start) aeQuery = aeQuery.gte('entry_date', dateRange.start);
  if (dateRange?.end) aeQuery = aeQuery.lte('entry_date', dateRange.end);

  const { data: accountEntries, error: aeErr } = await aeQuery;
  if (aeErr) throw aeErr;

  return {
    billingRecords: billingRecords || [],
    accountEntries: accountEntries || [],
  };
}

/**
 * Generate CSV content for a pilot's billing data within a date range.
 * Fetches the data via getPilotBillingHistory and formats it as CSV.
 *
 * @param {string} pilotId
 * @param {{ start: string, end: string }} dateRange - YYYY-MM-DD bounds
 * @returns {Promise<string>} CSV content
 * Requirements: 15.4
 */
export async function exportBillingCSV(pilotId, dateRange) {
  const { billingRecords, accountEntries } = await getPilotBillingHistory(pilotId, dateRange);

  // Build CSV rows: Date, Description, Debit, Credit, Balance
  const rows = ['Date,Description,Debit,Credit,Balance'];
  const items = [];

  for (const r of billingRecords) {
    const amount = Number(r.amount_due) || 0;
    const aircraft = r.aircraft?.registration || 'N/A';
    items.push({
      date: r.period_start,
      description: `${aircraft} flying charges (${r.total_billable_mins} mins)`,
      debit: amount,
      credit: 0,
    });
  }

  for (const e of accountEntries) {
    const amt = Number(e.amount) || 0;
    items.push({
      date: e.entry_date,
      description: e.description || (e.entry_type === 'CREDIT' ? 'Credit' : 'Debit'),
      debit: e.entry_type === 'DEBIT' ? amt : 0,
      credit: e.entry_type === 'CREDIT' ? amt : 0,
    });
  }

  items.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  let balance = 0;
  for (const item of items) {
    balance = balance + item.credit - item.debit;
    const escapedDesc = `"${(item.description || '').replace(/"/g, '""')}"`;
    rows.push(`${item.date},${escapedDesc},${item.debit.toFixed(2)},${item.credit.toFixed(2)},${balance.toFixed(2)}`);
  }

  return rows.join('\n');
}



// ── Billing Record Lines ──────────────────────────────

export async function getBillingRecordLines(billingRecordId) {
  const { data, error } = await getSupabase()
    .from('billing_record_lines')
    .select('*')
    .eq('billing_record_id', billingRecordId)
    .order('created_at');
  if (error) throw error;
  return data;
}

export async function createBillingRecordLine(line) {
  const { data, error } = await getSupabase()
    .from('billing_record_lines')
    .insert(line)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateBillingRecordLine(id, updates) {
  const { data, error } = await getSupabase()
    .from('billing_record_lines')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteBillingRecordLine(id) {
  const { error } = await getSupabase()
    .from('billing_record_lines')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ── Payment Allocations ───────────────────────────────

export async function createPaymentAllocation(allocation) {
  const { data, error } = await getSupabase()
    .from('payment_allocations')
    .insert(allocation)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getPaymentAllocations(invoiceId) {
  const { data, error } = await getSupabase()
    .from('payment_allocations')
    .select('*, pilot_account_entries:account_entry_id(entry_date, description, amount)')
    .eq('invoice_id', invoiceId)
    .order('created_at');
  if (error) throw error;
  return data;
}

export async function getPaymentAllocationsForEntry(accountEntryId) {
  const { data, error } = await getSupabase()
    .from('payment_allocations')
    .select('*, invoices:invoice_id(id, total_amount, paid_amount, status)')
    .eq('account_entry_id', accountEntryId)
    .order('created_at');
  if (error) throw error;
  return data;
}

export async function getOutstandingInvoices(pilotId) {
  const { data, error } = await getSupabase()
    .from('invoices')
    .select('*, pilots:pilot_id(name)')
    .eq('pilot_id', pilotId)
    .in('status', ['DRAFT', 'ISSUED', 'PARTIAL'])
    .order('period_start');
  if (error) throw error;
  return data;
}

export async function updateInvoice(invoiceId, updates) {
  const { data, error } = await getSupabase()
    .from('invoices')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', invoiceId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Fuel Costs ────────────────────────────────────────

export async function getFuelCosts() {
  const { data, error } = await getSupabase()
    .from('fuel_costs')
    .select('*')
    .order('month', { ascending: false });
  if (error) throw error;
  return data;
}

export async function setFuelCost(month, costPerLitre) {
  const { data, error } = await getSupabase()
    .from('fuel_costs')
    .upsert({ month, cost_per_litre: costPerLitre }, { onConflict: 'month' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Pilot Account Entries ─────────────────────────────

/**
 * Fetch ALL pilot account entries across all pilots.
 * Used by admin billing summary to aggregate credits per pilot.
 *
 * @returns {Promise<Object[]>} All account entries with pilot name joined
 * Requirements: 13.1
 */
export async function getAllAccountEntries() {
  const { data, error } = await getSupabase()
    .from('pilot_account_entries')
    .select('*, pilots:pilot_id(name)')
    .order('entry_date', { ascending: false });
  if (error) throw error;
  return data;
}

export async function createAccountEntry(pilotId, entry) {
  const { data, error } = await getSupabase()
    .from('pilot_account_entries')
    .insert({ pilot_id: pilotId, ...entry })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Owner Aircraft Management ─────────────────────────

/**
 * Create a new aircraft with all fields including fuel tank config,
 * oil settings, and maintenance status.
 * @param {Object} data - Full aircraft payload
 * @returns {Promise<Object>} Created aircraft record
 */
export async function createAircraftFull(data) {
  const record = {
    registration: data.registration,
    type: data.type,
    name: data.name,
    status: data.status || 'SERVICEABLE',
    location: data.location || null,
    image_url: data.image_url || null,
    airframe_hours: data.airframe_hours || 0,
    engine_hours: data.engine_hours || 0,
    propeller_hours: data.propeller_hours || 0,
    total_hours: data.airframe_hours || 0,
    hours_since_last_50hr: data.hours_since_last_50hr || 0,
    last_annual_date: data.last_annual_date || null,
    next_oil_change_hours: data.next_oil_change_hours || null,
    fuel_tank_config: data.fuel_tank_config || { tanks: ['main'] },
    oil_measurement_unit: data.oil_measurement_unit || 'quarts',
    max_oil_capacity: data.max_oil_capacity || 0,
    archived: false,
    meta: data.meta || {},
  };

  const { data: created, error } = await getSupabase()
    .from('aircraft')
    .insert(record)
    .select()
    .single();
  if (error) throw error;
  return created;
}

/**
 * Update an aircraft with all fields including fuel tank config,
 * oil settings, and maintenance status.
 * @param {string} id - Aircraft UUID
 * @param {Object} data - Fields to update
 * @returns {Promise<Object>} Updated aircraft record
 */
export async function updateAircraftFull(id, data) {
  // Merge meta if provided
  if (data.meta) {
    const { data: existing, error: fetchError } = await getSupabase()
      .from('aircraft')
      .select('meta')
      .eq('id', id)
      .single();
    if (fetchError) throw fetchError;
    data.meta = { ...(existing.meta || {}), ...data.meta };
  }

  // Keep total_hours in sync with airframe_hours
  if (data.airframe_hours !== undefined) {
    data.total_hours = data.airframe_hours;
  }

  const { data: updated, error } = await getSupabase()
    .from('aircraft')
    .update({ ...data, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return updated;
}

// ── Charges ───────────────────────────────────────────

export async function getCharges() {
  const { data, error } = await getSupabase()
    .from('charges')
    .select('*, aircraft(registration)')
    .order('period', { ascending: false })
    .order('charge_type');
  if (error) throw error;
  return data;
}

export async function getChargeTypes() {
  const { data, error } = await getSupabase()
    .from('charges')
    .select('charge_type');
  if (error) throw error;
  return [...new Set(data.map(r => r.charge_type))].sort();
}

export async function upsertCharge(charge) {
  const { id, ...fields } = charge;
  if (id) {
    const { data, error } = await getSupabase()
      .from('charges')
      .update({ ...fields, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  }
  const { data, error } = await getSupabase()
    .from('charges')
    .insert(fields)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteCharge(id) {
  const { error } = await getSupabase()
    .from('charges')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ── Merge, Alias, Password & User Detail ──────────────

/**
 * Merge pilots by converting non-parent pilots into alias records.
 * Sets each alias pilot's role to 'alias', parent_id to the surviving parent,
 * active to false, and description to the provided or default value.
 *
 * Existing flight records, licences, and billing remain linked to the
 * original pilot_id — no re-linking is performed.
 *
 * @param {string} parentId - UUID of the surviving parent user
 * @param {string[]} aliasIds - UUIDs of pilots to convert to aliases
 * @param {Object<string, string>} [descriptions={}] - Optional per-alias descriptions keyed by pilot ID
 */
export async function mergePilots(parentId, aliasIds, descriptions = {}) {
  const supabase = getSupabase();
  for (const aliasId of aliasIds) {
    const desc = descriptions[aliasId] || `Merged from import`;
    const { error } = await supabase
      .from('pilots')
      .update({
        role: 'alias',
        parent_id: parentId,
        active: false,
        description: desc,
        updated_at: new Date().toISOString(),
      })
      .eq('id', aliasId);
    if (error) throw error;
  }
}

/**
 * Set a user's password directly via the Supabase Auth admin API.
 *
 * @param {string} userId - The auth user's UUID
 * @param {string} newPassword - The new password to set
 */
export async function setUserPassword(userId, newPassword) {
  const { error } = await getSupabase().auth.admin.updateUserById(userId, { password: newPassword });
  if (error) throw error;
}

/**
 * Fetch all alias records that point to a given parent user.
 *
 * @param {string} parentId - UUID of the parent pilot
 * @returns {Promise<Object[]>} Alias pilot records ordered by name
 */
export async function getPilotAliases(parentId) {
  const { data, error } = await getSupabase()
    .from('pilots')
    .select('*')
    .eq('parent_id', parentId)
    .order('name');
  if (error) throw error;
  return data;
}

/**
 * Compute a pilot's account balance from pilot_account_entries.
 * Balance = sum(CREDIT amounts) − sum(DEBIT amounts).
 *
 * @param {string} pilotId - The pilot's UUID
 * @returns {Promise<{ credits: number, debits: number, balance: number }>}
 */
export async function getPilotAccountBalance(pilotId) {
  const { data, error } = await getSupabase()
    .from('pilot_account_entries')
    .select('entry_type, amount')
    .eq('pilot_id', pilotId);
  if (error) throw error;
  const credits = data.filter(e => e.entry_type === 'CREDIT').reduce((s, e) => s + Number(e.amount), 0);
  const debits = data.filter(e => e.entry_type === 'DEBIT').reduce((s, e) => s + Number(e.amount), 0);
  return { credits, debits, balance: credits - debits };
}

/**
 * Fetch flights for a user and optionally their aliases.
 * Returns all flights where pilot_id matches the user or any of their alias IDs.
 *
 * @param {string} pilotId - The primary pilot's UUID
 * @param {string[]} [aliasIds=[]] - UUIDs of alias records to include
 * @returns {Promise<Object[]>} Flight records with aircraft and pilot joins
 */
export async function getFlightsForUser(pilotId, aliasIds = []) {
  const allIds = [pilotId, ...aliasIds];
  const { data, error } = await getSupabase()
    .from('flights')
    .select('*, aircraft(registration, type), pilots!pilot_id(name)')
    .in('pilot_id', allIds)
    .order('flight_date', { ascending: false });
  if (error) throw error;
  return data;
}
